import { eventCategories } from '@/constants';
import { MarketStatus } from '@sportspredict/shared';
import { format, isPast } from 'date-fns';

export const formatDate = (dateString: any) => {
	try {
		return format(new Date(dateString), 'MMM d, yyyy h:mm a');
	} catch {
		return 'Invalid date';
	}
};

export const copyToClipboard = (text: any) => {
	navigator.clipboard.writeText(text);
	// toast({
	// 	title: 'Copied to clipboard',
	// 	description: 'ID has been copied to your clipboard',
	// });
};

export const safeDateFormat = (dateString: any, formatStr: any) => {
	try {
		if (!dateString) return 'Date not set';
		const date = new Date(dateString);
		if (isNaN(date.getTime())) return 'Invalid date';
		return format(date, formatStr);
	} catch (error) {
		console.error(error);
		return 'Invalid date';
	}
};

export const getMarketStatus = (market: any) => {
	if (market.status === MarketStatus.Settled) return 'Settled';
	if (market.status === MarketStatus.Closed) return 'Closed';
	if (market.status === MarketStatus.Queue) return 'Queued';

	if (market.opening_time && !isPast(new Date(market.opening_time))) {
		return 'Opens ' + format(new Date(market.opening_time), "MMM d 'at' h:mm a");
	}

	return 'Open';
};

export const formatImportDate = (str: any) => {
	if (!str || typeof str !== 'string') return '';
	const formatted = str.includes(' ') ? str.replace(' ', 'T') : str;
	const date = new Date(formatted);
	return isNaN(date.getTime()) ? '' : formatted;
};

export const parseCSVLine = (line: any) => {
	const result = [];
	let current = '';
	let inQuotes = false;

	for (let i = 0; i < line.length; i++) {
		const char = line[i];
		const nextChar = line[i + 1];

		if (char === '"' && inQuotes && nextChar === '"') {
			current += '"';
			i++;
		} else if (char === '"') {
			inQuotes = !inQuotes;
		} else if (char === ',' && !inQuotes) {
			result.push(current.trim());
			current = '';
		} else {
			current += char;
		}
	}
	result.push(current.trim());
	return result;
};

export const getCategoryName = (category_id: any) =>
	category_id ? eventCategories.find((item: any) => item.id === category_id)?.name : '';

export const getCategory = (category_id: any) =>
	category_id ? eventCategories.find((item: any) => item.id === category_id) : '';

export const getEventOpeningTime = (event: any): Date | null => {
	const value = event?.opening_time ?? event?.displayed_start_time;
	return value ? new Date(value) : null;
};

export const validateMarketDates = (event: any, market: any) => {
	if (!event || !market) {
		return false;
	}

	const eventStartDate = getEventOpeningTime(event) ?? new Date(event.displayed_start_time);
	const eventEndDate = new Date(event.end_date);
	const marketStartDate = market.opening_time ? new Date(market.opening_time) : null;
	const marketEndDate = market.closing_date ? new Date(market.closing_date) : null;

	const isMarketStartInRange =
		!marketStartDate || (marketStartDate >= eventStartDate && marketStartDate <= eventEndDate);

	const isMarketEndInRange =
		!marketEndDate || (marketEndDate >= eventStartDate && marketEndDate <= eventEndDate);

	const isStartBeforeEnd = !marketStartDate || !marketEndDate || marketStartDate <= marketEndDate;

	return isMarketStartInRange && isMarketEndInRange && isStartBeforeEnd;
};

export const validateMarketDatesOrToast = (
	event: any,
	market: any,
	toast: any,
	description = 'Market dates must be within event date range'
) => {
	if (validateMarketDates(event, market)) return true;

	toast({ variant: 'destructive', title: 'Validation Error', description });
	return false;
};

export const findEarliestOpeningAndLatestClosing = (slate: any) => {
	if (!slate?.matches?.length) {
		return { earliestOpening: null, latestClosing: null };
	}

	let earliestOpening: string | null = null;
	let latestClosing: string | null = null;

	slate.matches.forEach((match: any) => {
		if (match.opening_time) {
			const openingDate = new Date(match.opening_time);
			if (!earliestOpening || openingDate < new Date(earliestOpening)) {
				earliestOpening = match.opening_time;
			}
		}

		if (match.closing_time) {
			const closingDate = new Date(match.closing_time);
			if (!latestClosing || closingDate > new Date(latestClosing)) {
				latestClosing = match.closing_time;
			}
		}
	});

	return { earliestOpening, latestClosing };
};

export const findMatch = (slates: any, matchId: any) => {
	for (const slate of slates) {
		const match = slate.matches.find((m: any) => m.id === matchId);
		if (match) return match;
	}
	return null;
};

export const chunkArray = (arr: any, size: any) => {
	const result = [];
	for (let i = 0; i < arr.length; i += size) {
		result.push(arr.slice(i, i + size));
	}
	return result;
};
